import { useEffect, useMemo, useRef } from "react";
import {
  useListItemRecipes,
  useListItemModifiers,
  useListInventoryItems,
  getListItemRecipesQueryKey,
  getListItemModifiersQueryKey,
} from "@workspace/api-client-react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Phase 55 Plan 55-05 (RECIPE-MODIFIER-01): counter-based ordering UI replacing
// the boolean checkbox list. A member can stack the same modifier any number of
// times (e.g. +Cheese ×3 on one Cheese Maggi); the count field flows through
// the CreateStockOutBody.appliedModifiers wire shape to the server, which
// multiplies qtyPerUse × count for stock decrement and addPrice × count for
// the member charge, atomically with the base recipe cascade. count=0 means
// the modifier is not applied — the entry is OMITTED from the emitted array
// (not stored as {count:0}) so the server never resolves a no-op row.
//
// Phase 56 Plan 56-04 (RECIPE-COMPOSITION-03/09): an entry MAY carry
// `chosenComponentItemId`. It is REQUIRED (client invariant, server 422s on
// violation) when the modifier's `logicType === 'OR' && count > 0`; it is
// FORBIDDEN when the modifier's `logicType === 'AND'`. This picker renders an
// inline <Select> beneath OR-modifier counter cards so the operator picks
// which linked component to decrement.
export interface AppliedModifierEntry {
  modifierId: number;
  count: number;
  chosenComponentItemId?: number;
}

interface StockOutModifierCheckboxesProps {
  itemId: number | null;
  basePrice: number;
  quantity: number;
  appliedModifiers: AppliedModifierEntry[];
  onChange: (next: AppliedModifierEntry[]) => void;
  onLineTotalChange?: (total: number) => void;
  // Phase 56 Plan 56-04 — validity callback used by the parent Stock-Out form
  // to disable Submit when any OR-modifier line has count > 0 but no chosen
  // component. Message names the FIRST offending modifier so the operator
  // knows where to look.
  onValidityChange?: (isValid: boolean, message?: string) => void;
}

function entryFor(
  list: AppliedModifierEntry[],
  id: number,
): AppliedModifierEntry | undefined {
  return list.find((m) => m.modifierId === id);
}

function countFor(list: AppliedModifierEntry[], id: number): number {
  return entryFor(list, id)?.count ?? 0;
}

// Preserves chosenComponentItemId across count changes — critical for the OR
// picker UX (operator picks Cheese Amul, then bumps count from 1→2 without
// having to re-pick).
function setCount(
  list: AppliedModifierEntry[],
  id: number,
  count: number,
): AppliedModifierEntry[] {
  const existing = entryFor(list, id);
  const next = list.filter((m) => m.modifierId !== id);
  if (count > 0) {
    next.push({
      modifierId: id,
      count,
      ...(existing?.chosenComponentItemId != null
        ? { chosenComponentItemId: existing.chosenComponentItemId }
        : {}),
    });
  }
  return next;
}

function setChosenComponent(
  list: AppliedModifierEntry[],
  id: number,
  chosenComponentItemId: number | null,
): AppliedModifierEntry[] {
  const existing = entryFor(list, id);
  const count = existing?.count ?? 0;
  if (count <= 0) return list; // no-op if modifier isn't applied
  const next = list.filter((m) => m.modifierId !== id);
  next.push({
    modifierId: id,
    count,
    ...(chosenComponentItemId != null ? { chosenComponentItemId } : {}),
  });
  return next;
}

export function StockOutModifierCheckboxes({
  itemId,
  basePrice,
  quantity,
  appliedModifiers,
  onChange,
  onLineTotalChange,
  onValidityChange,
}: StockOutModifierCheckboxesProps) {
  const { data: recipes = [] } = useListItemRecipes(
    itemId ?? 0,
    {
      query: {
        queryKey: getListItemRecipesQueryKey(itemId ?? 0),
        enabled: itemId != null,
      },
    }
  );

  const isRecipe = recipes.length > 0;

  const { data: modifiers = [] } = useListItemModifiers(
    itemId ?? 0,
    { status: "Active" },
    {
      query: {
        queryKey: getListItemModifiersQueryKey(itemId ?? 0, { status: "Active" }),
        enabled: itemId != null && isRecipe,
      },
    }
  );

  // Phase 56 — resolve component id → item name/stock for the OR picker's
  // row template. Consumes the full active-items list (already cached CRM-wide
  // via the same hook the parent uses); lookup is O(component-count) per
  // render — trivial for the 2-4 typical component count.
  const { data: allItems = [] } = useListInventoryItems({ status: "Active" });
  const itemsById = useMemo(() => {
    const map = new Map<number, { name: string; brand?: string | null; currentStock?: string | number | null }>();
    for (const it of allItems as Array<{ id: number; name: string; brand?: string | null; currentStock?: string | number | null }>) {
      map.set(it.id, { name: it.name, brand: it.brand, currentStock: it.currentStock });
    }
    return map;
  }, [allItems]);

  const lineTotal = useMemo(() => {
    const modifierSum = appliedModifiers.reduce((sum, entry) => {
      const mod = modifiers.find((x) => x.id === entry.modifierId);
      return sum + (mod ? Number(mod.addPrice) * entry.count : 0);
    }, 0);
    const total = (basePrice + modifierSum) * quantity;
    onLineTotalChange?.(total);
    return total;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedModifiers, basePrice, quantity, modifiers]);

  // Phase 56 — validity signal for parent Submit-disable. Fires on every
  // change to `appliedModifiers` or `modifiers`. Empty state (no OR modifiers
  // applied) → isValid=true.
  const lastValidityRef = useRef<{ isValid: boolean; message?: string } | null>(null);
  useEffect(() => {
    if (!onValidityChange) return;
    let firstOffender: string | null = null;
    for (const entry of appliedModifiers) {
      if (entry.count <= 0) continue;
      const mod = modifiers.find((m) => m.id === entry.modifierId);
      if (!mod) continue;
      if (mod.logicType === "OR" && entry.chosenComponentItemId == null) {
        firstOffender = mod.modifierName;
        break;
      }
    }
    const isValid = firstOffender == null;
    const message = firstOffender
      ? `Pick a component for ${firstOffender}`
      : undefined;
    const prev = lastValidityRef.current;
    if (!prev || prev.isValid !== isValid || prev.message !== message) {
      lastValidityRef.current = { isValid, message };
      onValidityChange(isValid, message);
    }
  }, [appliedModifiers, modifiers, onValidityChange]);

  // If no item selected or not a recipe item, render nothing
  if (itemId == null || !isRecipe) return null;

  // If recipe item but no modifiers, render nothing (no "no modifiers" text per spec)
  if (modifiers.length === 0) return null;

  return (
    <div className="space-y-2 rounded-md border p-3">
      <Label className="text-sm font-medium">Add-ons</Label>
      <div className="space-y-2">
        {modifiers.map((m) => {
          const count = countFor(appliedModifiers, m.id);
          const entry = entryFor(appliedModifiers, m.id);
          const dec = () => onChange(setCount(appliedModifiers, m.id, Math.max(0, count - 1)));
          const inc = () => onChange(setCount(appliedModifiers, m.id, count + 1));
          const components = m.components ?? [];
          const showOrPicker = m.logicType === "OR" && count > 0;
          const orMissing = showOrPicker && entry?.chosenComponentItemId == null;
          return (
            <div key={m.id} className="space-y-1">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 w-7 p-0"
                    aria-label={`Decrease ${m.modifierName}`}
                    onClick={dec}
                    disabled={count === 0}
                  >
                    -
                  </Button>
                  <span
                    className="min-w-[1.5rem] text-center text-sm tabular-nums"
                    aria-live="polite"
                  >
                    {count}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 w-7 p-0"
                    aria-label={`Increase ${m.modifierName}`}
                    onClick={inc}
                  >
                    +
                  </Button>
                </div>
                <span className="text-sm">
                  + {m.modifierName} ₹{Number(m.addPrice).toFixed(2)}
                </span>
                {m.logicType === "OR" && (
                  <span className="text-[10px] font-medium rounded px-1.5 py-0.5 border border-amber-300 bg-amber-50 text-amber-900 tabular-nums">
                    OR
                  </span>
                )}
                {components.length === 0 && m.componentItemId == null && (
                  <span className="text-xs text-muted-foreground">(annotation only)</span>
                )}
              </div>

              {showOrPicker && (
                <div className="ml-8 space-y-1">
                  <Label className="text-xs text-muted-foreground">Pick component</Label>
                  <Select
                    value={
                      entry?.chosenComponentItemId != null
                        ? String(entry.chosenComponentItemId)
                        : ""
                    }
                    onValueChange={(v) =>
                      onChange(
                        setChosenComponent(
                          appliedModifiers,
                          m.id,
                          v ? Number(v) : null,
                        ),
                      )
                    }
                  >
                    <SelectTrigger className={orMissing ? "border-destructive" : undefined}>
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent>
                      {components.map((c) => {
                        const it = itemsById.get(c.componentItemId);
                        const stock =
                          it?.currentStock != null
                            ? Number(it.currentStock)
                            : null;
                        const label = it
                          ? `${it.name}${it.brand ? ` · ${it.brand}` : ""}`
                          : `Item #${c.componentItemId}`;
                        return (
                          <SelectItem
                            key={c.componentItemId}
                            value={String(c.componentItemId)}
                          >
                            {label} —{" "}
                            {stock != null && Number.isFinite(stock)
                              ? `${stock} in stock`
                              : "—"}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  {orMissing && (
                    <p className="text-[11px] text-destructive">
                      Required for OR modifiers
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="border-t pt-2 font-medium text-sm">
        Line total: ₹{lineTotal.toFixed(2)}
      </div>
    </div>
  );
}
